import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogContent, DialogActions, useTheme,
} from '@mui/material';
import { Fullscreen as FullscreenIcon } from '@mui/icons-material';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import { EditorView } from '@codemirror/view';
import MarkdownContent from './MarkdownContent';

interface MarkdownCodeEditorProps {
  label?: string;
  value: string;
  onChange: (next: string) => void;
  minHeight?: number;
  readOnly?: boolean;
  /**
   * What describes the document, above it and switching with it: the text when reading, its
   * field when writing. It is never part of the source the editor saves.
   */
  preamble?: { text: string; field?: React.ReactNode };
}

/** Markdown editor built on the same CodeMirror dependency as JsonCodeEditor (rule patterns). */
const MarkdownCodeEditor: React.FC<MarkdownCodeEditorProps> = ({
  label,
  value,
  onChange,
  minHeight = 220,
  readOnly = false,
  preamble,
}) => {
  const theme = useTheme();
  const [preview, setPreview] = useState(false);

  const cmTheme = useMemo(
    () =>
      EditorView.theme(
        {
          '&': { backgroundColor: 'transparent', fontSize: '0.8rem', color: theme.palette.text.primary },
          '.cm-content': { fontFamily: '"JetBrains Mono", "Roboto Mono", monospace', caretColor: theme.palette.primary.main },
          '.cm-gutters': { backgroundColor: 'transparent', color: theme.palette.text.disabled, border: 'none' },
          '.cm-activeLine': { backgroundColor: theme.palette.action.hover },
          '.cm-activeLineGutter': { backgroundColor: theme.palette.action.hover },
          '&.cm-focused': { outline: 'none' },
          '.cm-cursor': { borderLeftColor: theme.palette.primary.main },
        },
        { dark: theme.palette.mode === 'dark' },
      ),
    [theme],
  );

  const extensions = useMemo(() => [markdown(), cmTheme, EditorView.lineWrapping], [cmTheme]);

  const describedReadOnly = (sx: object) => preamble?.text.trim() ? (
    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', ...sx }}>
      {preamble.text}
    </Typography>
  ) : null;

  const rendered = (height: number | string) => (
      <Box
        sx={{
          border: 1, borderColor: 'divider', borderRadius: 2, p: 2,
          minHeight: height, maxHeight: height, overflow: 'auto', bgcolor: 'background.paper',
        }}
      >
        {describedReadOnly({ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' })}
        {value.trim()
          ? <MarkdownContent content={value} variant="document" syntaxHighlight />
          : <Typography variant="body2" color="text.disabled">Nothing to preview yet.</Typography>}
      </Box>
  );

  const source = (height: number) => (
      <Box>
      {preamble?.field && <Box sx={{ mb: 1 }}>{preamble.field}</Box>}
      <Box
        sx={{
          border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden',
          '&:focus-within': { borderColor: 'primary.main' },
          '& .cm-editor': { minHeight: height },
        }}
      >
        <CodeMirror
          value={value}
          onChange={onChange}
          editable={!readOnly}
          theme={theme.palette.mode === 'dark' ? 'dark' : 'light'}
          extensions={extensions}
          basicSetup={{
            lineNumbers: true,
            foldGutter: false,
            highlightActiveLine: !readOnly,
            bracketMatching: false,
            closeBrackets: false,
            autocompletion: false,
            highlightActiveLineGutter: !readOnly,
          }}
        />
      </Box>
      </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{label}</Typography>
        {/* One view inline, which is the one you can type in. Reading it whole is what the
            expanded view is for, so there is nothing to choose between. */}
        <Button
          size="small"
          startIcon={<FullscreenIcon />}
          onClick={() => setPreview(true)}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          Read it whole
        </Button>
      </Box>
      {source(minHeight)}

      <Dialog
        open={preview}
        onClose={() => setPreview(false)}
        maxWidth="lg"
        fullWidth
        aria-label={label ? `${label}, rendered` : 'The prompt, rendered'}
      >
        <DialogContent>{rendered('70vh')}</DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarkdownCodeEditor;
